import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Group } from 'three';
import { Points, PointMaterial } from '@react-three/drei';
import { Points as ThreePoints } from 'three';
import { getOptimalParticleCount } from '../../utils/deviceDetection';

interface CardPack3DProps {
  cards: any[];
  onOpenComplete: () => void;
  isOpening: boolean;
  openProgress: number; // 0-1, 사용자가 드래그한 정도
  position?: [number, number, number];
}

// 모래 입자 생성 함수 (원뿔 형태)
function generateSandParticles(count: number, baseRadius: number, height: number, openProgress: number): Float32Array {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const radius = baseRadius * (0.3 + Math.random() * 0.7) * (1 - openProgress * 0.5);
    const y = (Math.random() - 0.5) * height * (1 + openProgress * 2); // 드래그하면 위로 확장
    
    positions[i * 3] = Math.cos(angle) * radius;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = Math.sin(angle) * radius;
  }
  return positions;
}

export function CardPack3D({ 
  cards, 
  onOpenComplete, 
  isOpening, 
  openProgress,
  position = [0, 0, 0]
}: CardPack3DProps) {
  const stormGroupRef = useRef<Group>(null);
  const coreRef = useRef<Mesh>(null);
  const particlesRef = useRef<ThreePoints>(null);
  
  // 모래 폭풍 크기
  const baseRadius = 0.8;
  const height = 2.5;
  
  // 모바일 최적화: 파티클 수 조정
  const baseParticleCount = 800;
  const optimalParticleCount = useMemo(() => getOptimalParticleCount(baseParticleCount), []);
  const additionalParticleCount = useMemo(() => getOptimalParticleCount(200), []);
  
  // 모래 입자 생성 (드래그 진행도에 따라 변화)
  const sandParticles = useMemo(() => {
    return generateSandParticles(optimalParticleCount, baseRadius, height, openProgress);
  }, [openProgress, optimalParticleCount]);
  
  // 모래 폭풍 애니메이션 (개선된 자연스러운 움직임)
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (stormGroupRef.current) {
      // 더 부드러운 회전 (드래그 중일 때 점진적으로 빠르게)
      const rotationSpeed = isOpening ? 0.025 + openProgress * 0.03 : 0.02;
      stormGroupRef.current.rotation.y += rotationSpeed;
      
      // 자연스러운 흔들림 (드래그 중일 때 더 강하게)
      const shakeIntensity = isOpening ? 0.04 + openProgress * 0.08 : 0.04;
      stormGroupRef.current.rotation.z = Math.sin(time * 0.4) * shakeIntensity;
      stormGroupRef.current.rotation.x = Math.cos(time * 0.25) * shakeIntensity * 0.6;
      
      // 위로 떠오르는 효과 (드래그 중일 때)
      if (isOpening && openProgress > 0.3) {
        stormGroupRef.current.position.y = Math.sin(time * 1.5) * 0.08 * openProgress;
      }
    }
    
    if (coreRef.current) {
      // 중심부 회전 (드래그 중일 때 더 빠르게)
      const coreRotationSpeed = isOpening ? 0.07 + openProgress * 0.06 : 0.05;
      coreRef.current.rotation.y += coreRotationSpeed;
      
      // 드래그 중일 때 부드러운 확장 효과
      if (isOpening) {
        const targetScale = 1 + openProgress * 0.35;
        const targetHeightScale = 1 + openProgress * 0.55; // 높이도 더 확장
        const currentScale = coreRef.current.scale.x;
        const currentHeightScale = coreRef.current.scale.y;
        coreRef.current.scale.set(
          currentScale + (targetScale - currentScale) * 0.1,
          currentHeightScale + (targetHeightScale - currentHeightScale) * 0.1,
          currentScale + (targetScale - currentScale) * 0.1
        );
      } else {
        const currentScale = coreRef.current.scale.x;
        const currentHeightScale = coreRef.current.scale.y;
        coreRef.current.scale.set(
          currentScale + (1 - currentScale) * 0.1,
          currentHeightScale + (1 - currentHeightScale) * 0.1,
          currentScale + (1 - currentScale) * 0.1
        );
    }
    
      // 중심부가 위로 떠오르는 효과 (더 부드럽게)
      if (isOpening && openProgress > 0) {
        const targetY = openProgress * 0.25;
        coreRef.current.position.y += (targetY - coreRef.current.position.y) * 0.1;
      } else {
        coreRef.current.position.y += (0 - coreRef.current.position.y) * 0.1;
      }
    }
    
    if (particlesRef.current) {
      // 모래 입자 회전 (드래그 중일 때 더 빠르게)
      const particleRotationSpeed = isOpening ? 0.018 + openProgress * 0.012 : 0.01;
      particlesRef.current.rotation.y += particleRotationSpeed;
      particlesRef.current.rotation.x = Math.sin(time * 0.25) * 0.12;
      
      // 드래그 중일 때 입자들이 위로 흩어지는 효과 (더 부드럽게)
      if (isOpening && openProgress > 0) {
        const targetY = openProgress * 0.7;
        particlesRef.current.position.y += (targetY - particlesRef.current.position.y) * 0.08;
      } else {
        particlesRef.current.position.y += (0 - particlesRef.current.position.y) * 0.08;
      }
    }
  });

  return (
    <group ref={stormGroupRef} position={position}>
      {/* 모래 폭풍 중심부 (원뿔 형태) */}
      <mesh ref={coreRef} position={[0, 0, 0]}>
        <coneGeometry args={[baseRadius, baseRadius * 0.3, 32, 1, true]} />
        <meshStandardMaterial 
          color="#d4af37" 
          metalness={0.1}
          roughness={0.9}
          emissive="#ffd700"
          emissiveIntensity={0.3 + openProgress * 0.3}
          transparent
          opacity={0.7 - openProgress * 0.3}
        />
      </mesh>
      
      {/* 모래 폭풍 외곽 (더 넓은 원뿔) */}
      <mesh position={[0, 0, 0]}>
        <coneGeometry args={[baseRadius * 1.2, baseRadius * 0.4, 32, 1, true]} />
        <meshStandardMaterial 
          color="#c9a961" 
          metalness={0.1}
          roughness={0.9}
          emissive="#d4af37"
          emissiveIntensity={0.2 + openProgress * 0.2}
          transparent
          opacity={0.5 - openProgress * 0.3}
        />
      </mesh>
      
      {/* 모래 입자 효과 */}
      <Points ref={particlesRef} positions={sandParticles} stride={3} frustumCulled={false}>
        <PointMaterial
          transparent
          color={openProgress > 0.5 ? "#ffd700" : "#d4af37"}
          size={0.03 + openProgress * 0.02}
          sizeAttenuation={true}
          depthWrite={false}
          opacity={0.8 - openProgress * 0.3}
        />
      </Points>
      
      {/* 드래그 중일 때 추가 모래 입자 (위로 흩어지는 효과) */}
      {isOpening && openProgress > 0.3 && (
        <Points 
          positions={generateSandParticles(additionalParticleCount, baseRadius * 0.5, height * 2, openProgress)} 
          stride={3}
          frustumCulled={false}
        >
          <PointMaterial
            transparent
            color="#ffd700"
            size={0.02}
            sizeAttenuation={true}
            depthWrite={false}
            opacity={0.6 * openProgress}
        />
        </Points>
      )}
    </group>
  );
}

